'use client';
import React, { useState } from 'react';
import { Calculator, BarChart2, Download, TrendingUp, Home, Building2, Factory, Layers, DollarSign, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const COUNTIES = ['All Counties', 'Montserrado', 'Margibi', 'Bong', 'Nimba', 'Lofa', 'Grand Bassa', 'Grand Gedeh'];
const PROPERTY_CLASSES = ['Residential', 'Commercial', 'Industrial', 'Mixed Use', 'Agricultural', 'Institutional'];
const ZONES = ['Zone A — CBD', 'Zone B — Urban', 'Zone C — Peri-Urban', 'Zone D — Rural', 'Zone E — Remote'];

interface ValuationResult {
  parcelId: string;
  owner: string;
  county: string;
  propertyClass: string;
  zone: string;
  landArea: number;
  buildingArea: number;
  baseRate: number;
  zoneMultiplier: number;
  classMultiplier: number;
  depreciationFactor: number;
  landValue: number;
  buildingValue: number;
  totalAssessedValue: number;
  annualTaxLiability: number;
  taxRate: number;
  lastValuation: string;
  valuationMethod: string;
}

// CAMA base rates per m² by zone (USD)
const ZONE_BASE_RATES: Record<string, number> = {
  'Zone A — CBD':       85,
  'Zone B — Urban':     55,
  'Zone C — Peri-Urban':32,
  'Zone D — Rural':     18,
  'Zone E — Remote':    8,
};

const ZONE_MULTIPLIERS: Record<string, number> = {
  'Zone A — CBD':       1.8,
  'Zone B — Urban':     1.4,
  'Zone C — Peri-Urban':1.1,
  'Zone D — Rural':     0.85,
  'Zone E — Remote':    0.6,
};

const CLASS_MULTIPLIERS: Record<string, number> = {
  Residential:   1.0,
  Commercial:    1.6,
  Industrial:    1.4,
  'Mixed Use':   1.3,
  Agricultural:  0.5,
  Institutional: 0.8,
};

const TAX_RATES: Record<string, number> = {
  Residential:   0.015,
  Commercial:    0.025,
  Industrial:    0.022,
  'Mixed Use':   0.020,
  Agricultural:  0.008,
  Institutional: 0.010,
};

const SAMPLE_VALUATIONS: ValuationResult[] = [
  { parcelId: 'LIB-MNT-001-2024', owner: 'Samuel Togba',    county: 'Montserrado', propertyClass: 'Commercial',  zone: 'Zone A — CBD',        landArea: 450,  buildingArea: 380,  baseRate: 85, zoneMultiplier: 1.8, classMultiplier: 1.6, depreciationFactor: 0.85, landValue: 68850,  buildingValue: 174624, totalAssessedValue: 243474, annualTaxLiability: 6087,  taxRate: 0.025, lastValuation: '2024-11-01', valuationMethod: 'CAMA Mass Appraisal' },
  { parcelId: 'LIB-MNT-003-2024', owner: 'John Kpehe',      county: 'Montserrado', propertyClass: 'Residential', zone: 'Zone B — Urban',       landArea: 320,  buildingArea: 210,  baseRate: 55, zoneMultiplier: 1.4, classMultiplier: 1.0, depreciationFactor: 0.90, landValue: 24640,  buildingValue: 58212,  totalAssessedValue: 82852,  annualTaxLiability: 1243,  taxRate: 0.015, lastValuation: '2024-11-01', valuationMethod: 'CAMA Mass Appraisal' },
  { parcelId: 'LIB-NMB-001-2024', owner: 'Nimba Mining Corp',county: 'Nimba',       propertyClass: 'Industrial',  zone: 'Zone C — Peri-Urban',  landArea: 8500, buildingArea: 4200, baseRate: 32, zoneMultiplier: 1.1, classMultiplier: 1.4, depreciationFactor: 0.80, landValue: 299200, buildingValue: 663936, totalAssessedValue: 963136, annualTaxLiability: 21189, taxRate: 0.022, lastValuation: '2024-10-15', valuationMethod: 'CAMA Mass Appraisal' },
  { parcelId: 'LIB-LOF-001-2024', owner: 'Mariama Konneh',  county: 'Lofa',        propertyClass: 'Residential', zone: 'Zone D — Rural',       landArea: 600,  buildingArea: 120,  baseRate: 18, zoneMultiplier: 0.85,classMultiplier: 1.0, depreciationFactor: 0.75, landValue: 9180,   buildingValue: 15390,  totalAssessedValue: 24570,  annualTaxLiability: 369,   taxRate: 0.015, lastValuation: '2024-10-20', valuationMethod: 'CAMA Mass Appraisal' },
  { parcelId: 'LIB-MRG-002-2024', owner: 'Agnes Flomo',     county: 'Margibi',     propertyClass: 'Commercial',  zone: 'Zone B — Urban',       landArea: 280,  buildingArea: 240,  baseRate: 55, zoneMultiplier: 1.4, classMultiplier: 1.6, depreciationFactor: 0.88, landValue: 30800,  buildingValue: 92467,  totalAssessedValue: 123267, annualTaxLiability: 3082,  taxRate: 0.025, lastValuation: '2024-11-05', valuationMethod: 'CAMA Mass Appraisal' },
  { parcelId: 'LIB-GBS-001-2024', owner: 'David Kpah',      county: 'Grand Bassa', propertyClass: 'Residential', zone: 'Zone C — Peri-Urban',  landArea: 500,  buildingArea: 180,  baseRate: 32, zoneMultiplier: 1.1, classMultiplier: 1.0, depreciationFactor: 0.82, landValue: 17600,  buildingValue: 47347,  totalAssessedValue: 64947,  annualTaxLiability: 974,   taxRate: 0.015, lastValuation: '2024-11-10', valuationMethod: 'CAMA Mass Appraisal' },
];

// CAMA Calculator
function computeValuation(landArea: number, buildingArea: number, propertyClass: string, zone: string, age: number): Partial<ValuationResult> {
  const baseRate = ZONE_BASE_RATES[zone] ?? 32;
  const zoneMultiplier = ZONE_MULTIPLIERS[zone] ?? 1.0;
  const classMultiplier = CLASS_MULTIPLIERS[propertyClass] ?? 1.0;
  const taxRate = TAX_RATES[propertyClass] ?? 0.015;
  const depreciationFactor = Math.max(0.4, 1 - (age * 0.015));
  const landValue = landArea * baseRate * zoneMultiplier;
  const buildingValue = buildingArea * baseRate * zoneMultiplier * classMultiplier * depreciationFactor;
  const totalAssessedValue = Math.round(landValue + buildingValue);
  const annualTaxLiability = Math.round(totalAssessedValue * taxRate);
  return { baseRate, zoneMultiplier, classMultiplier, depreciationFactor, landValue: Math.round(landValue), buildingValue: Math.round(buildingValue), totalAssessedValue, annualTaxLiability, taxRate };
}

const CLASS_ICONS: Record<string, React.ElementType> = {
  Residential: Home,
  Commercial: Building2,
  Industrial: Factory,
  'Mixed Use': Layers,
  Agricultural: MapPin,
  Institutional: Building2,
};

export default function AutoValuationClient() {
  const [selectedCounty, setSelectedCounty] = useState('All Counties');
  const [selectedClass, setSelectedClass] = useState('All Classes');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [calcOpen, setCalcOpen] = useState(false);

  // Calculator state
  const [calcLandArea, setCalcLandArea] = useState('500');
  const [calcBuildingArea, setCalcBuildingArea] = useState('200');
  const [calcClass, setCalcClass] = useState('Residential');
  const [calcZone, setCalcZone] = useState('Zone B — Urban');
  const [calcAge, setCalcAge] = useState('10');
  const [calcResult, setCalcResult] = useState<Partial<ValuationResult> | null>(null);

  const filtered = SAMPLE_VALUATIONS.filter(v => {
    const matchCounty = selectedCounty === 'All Counties' || v.county === selectedCounty;
    const matchClass = selectedClass === 'All Classes' || v.propertyClass === selectedClass;
    return matchCounty && matchClass;
  });

  const totalPortfolioValue = SAMPLE_VALUATIONS.reduce((s, v) => s + v.totalAssessedValue, 0);
  const totalTaxLiability = SAMPLE_VALUATIONS.reduce((s, v) => s + v.annualTaxLiability, 0);
  const avgValue = Math.round(totalPortfolioValue / SAMPLE_VALUATIONS.length);

  const handleCalculate = () => {
    const result = computeValuation(
      parseFloat(calcLandArea) || 0,
      parseFloat(calcBuildingArea) || 0,
      calcClass,
      calcZone,
      parseFloat(calcAge) || 0,
    );
    setCalcResult(result);
  };

  // Class breakdown
  const classSummary = PROPERTY_CLASSES.map(cls => {
    const items = SAMPLE_VALUATIONS.filter(v => v.propertyClass === cls);
    return {
      cls,
      count: items.length,
      totalValue: items.reduce((s, v) => s + v.totalAssessedValue, 0),
      totalTax: items.reduce((s, v) => s + v.annualTaxLiability, 0),
    };
  }).filter(c => c.count > 0);

  return (
    <div className="p-6 space-y-6" style={{ color: 'var(--dark-text)' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--dark-text)' }}>
            <Calculator size={22} style={{ color: '#34d399' }} />
            Auto-Valuation Engine
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dark-muted)' }}>
            CAMA-style mass appraisal · Property value by class and zone · Annual tax liability computation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCalcOpen(!calcOpen)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors" style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}>
            <Calculator size={14} /> CAMA Calculator
          </button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Portfolio Value', value: `$${(totalPortfolioValue / 1000).toFixed(0)}K`, color: '#34d399', icon: DollarSign },
          { label: 'Annual Tax Liability', value: `$${totalTaxLiability.toLocaleString()}`, color: '#60a5fa', icon: TrendingUp },
          { label: 'Avg Property Value', value: `$${(avgValue / 1000).toFixed(0)}K`, color: '#f97316', icon: BarChart2 },
          { label: 'Parcels Valued', value: SAMPLE_VALUATIONS.length, color: '#a78bfa', icon: Layers },
        ].map(kpi => (
          <div key={kpi.label} className="rounded-xl p-4 flex flex-col gap-2" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</span>
              <kpi.icon size={14} style={{ color: kpi.color }} />
            </div>
            <span className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</span>
          </div>
        ))}
      </div>

      {/* CAMA Calculator Panel */}
      {calcOpen && (
        <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid rgba(52,211,153,0.3)' }}>
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: '#34d399' }}>
            <Calculator size={14} /> CAMA Mass Appraisal Calculator
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--dark-muted)' }}>Property Parameters</h3>
              {[
                { label: 'Land Area (m²)', value: calcLandArea, setter: setCalcLandArea, type: 'number' },
                { label: 'Building Area (m²)', value: calcBuildingArea, setter: setCalcBuildingArea, type: 'number' },
                { label: 'Building Age (years)', value: calcAge, setter: setCalcAge, type: 'number' },
              ].map(f => (
                <div key={f.label}>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--dark-muted)' }}>{f.label}</label>
                  <input
                    type={f.type}
                    value={f.value}
                    onChange={e => f.setter(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-transparent outline-none"
                    style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
                  />
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--dark-muted)' }}>Classification</h3>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--dark-muted)' }}>Property Class</label>
                <select value={calcClass} onChange={e => setCalcClass(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm bg-transparent outline-none" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-text)', backgroundColor: 'var(--dark-elevated)' }}>
                  {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--dark-muted)' }}>Zone</label>
                <select value={calcZone} onChange={e => setCalcZone(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm bg-transparent outline-none" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-text)', backgroundColor: 'var(--dark-elevated)' }}>
                  {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div className="p-3 rounded-lg text-xs space-y-1" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                <div className="flex justify-between"><span style={{ color: 'var(--dark-muted)' }}>Base Rate (m²)</span><span style={{ color: '#34d399' }}>${ZONE_BASE_RATES[calcZone] ?? 32}</span></div>
                <div className="flex justify-between"><span style={{ color: 'var(--dark-muted)' }}>Zone Multiplier</span><span style={{ color: '#60a5fa' }}>×{ZONE_MULTIPLIERS[calcZone] ?? 1.0}</span></div>
                <div className="flex justify-between"><span style={{ color: 'var(--dark-muted)' }}>Class Multiplier</span><span style={{ color: '#f97316' }}>×{CLASS_MULTIPLIERS[calcClass] ?? 1.0}</span></div>
                <div className="flex justify-between"><span style={{ color: 'var(--dark-muted)' }}>Tax Rate</span><span style={{ color: '#a78bfa' }}>{((TAX_RATES[calcClass] ?? 0.015) * 100).toFixed(1)}%</span></div>
              </div>
              <button onClick={handleCalculate} className="w-full py-2.5 rounded-lg text-sm font-bold transition-colors" style={{ backgroundColor: '#34d399', color: '#000' }}>
                Compute Valuation
              </button>
            </div>
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--dark-muted)' }}>Valuation Result</h3>
              {calcResult ? (
                <div className="space-y-2">
                  {[
                    { label: 'Land Value', value: `$${calcResult.landValue?.toLocaleString()}`, color: '#f97316' },
                    { label: 'Building Value', value: `$${calcResult.buildingValue?.toLocaleString()}`, color: '#60a5fa' },
                    { label: 'Depreciation Factor', value: `${((calcResult.depreciationFactor ?? 1) * 100).toFixed(0)}%`, color: '#f59e0b' },
                    { label: 'Total Assessed Value', value: `$${calcResult.totalAssessedValue?.toLocaleString()}`, color: '#34d399' },
                    { label: 'Annual Tax Liability', value: `$${calcResult.annualTaxLiability?.toLocaleString()}`, color: '#a78bfa' },
                  ].map(r => (
                    <div key={r.label} className="flex justify-between items-center p-2 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{r.label}</span>
                      <span className="text-sm font-bold" style={{ color: r.color }}>{r.value}</span>
                    </div>
                  ))}
                  <div className="p-2 rounded-lg text-xs text-center" style={{ backgroundColor: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', color: '#34d399' }}>
                    Method: CAMA Mass Appraisal · {new Date().toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-center" style={{ color: 'var(--dark-muted)' }}>
                  <Calculator size={28} className="mb-2 opacity-40" />
                  <p className="text-xs">Enter parameters and click Compute</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CAMA Formula Explainer */}
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--dark-muted)' }}>CAMA Valuation Formula</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          {[
            { label: 'Land Value', formula: 'Land Area × Base Rate × Zone Multiplier', color: '#f97316' },
            { label: 'Building Value', formula: 'Building Area × Base Rate × Zone × Class × Depreciation', color: '#60a5fa' },
            { label: 'Annual Tax', formula: 'Total Assessed Value × Property Class Tax Rate', color: '#34d399' },
          ].map(f => (
            <div key={f.label} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
              <div className="font-semibold mb-1" style={{ color: f.color }}>{f.label}</div>
              <div className="font-mono" style={{ color: 'var(--dark-muted)' }}>{f.formula}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Class Breakdown */}
      <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--dark-text)' }}>Portfolio by Property Class</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {classSummary.map(c => {
            const ClassIcon = CLASS_ICONS[c.cls] ?? Building2;
            return (
              <div key={c.cls} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <ClassIcon size={16} style={{ color: '#34d399' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{c.cls}</span>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399' }}>{c.count} parcels</span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--dark-muted)' }}>Total Value</span>
                    <span className="font-bold" style={{ color: '#34d399' }}>${c.totalValue.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--dark-muted)' }}>Annual Tax</span>
                    <span className="font-bold" style={{ color: '#60a5fa' }}>${c.totalTax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--dark-muted)' }}>Tax Rate</span>
                    <span className="font-bold" style={{ color: '#f97316' }}>{((TAX_RATES[c.cls] ?? 0.015) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={selectedCounty} onChange={e => setSelectedCounty(e.target.value)} className="px-3 py-2 rounded-lg text-sm bg-transparent outline-none" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-text)', backgroundColor: 'var(--dark-surface)' }}>
          {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="px-3 py-2 rounded-lg text-sm bg-transparent outline-none" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-text)', backgroundColor: 'var(--dark-surface)' }}>
          <option value="All Classes">All Classes</option>
          {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Valuation Records */}
      <div className="space-y-3">
        {filtered.map(v => {
          const VIcon = CLASS_ICONS[v.propertyClass] ?? Building2;
          const isExpanded = expanded === v.parcelId;
          return (
            <div key={v.parcelId} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)', border: `1px solid ${isExpanded ? '#34d399' : 'var(--dark-border)'}` }}>
              <button
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors"
                onClick={() => setExpanded(isExpanded ? null : v.parcelId)}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(52,211,153,0.12)' }}>
                  <VIcon size={16} style={{ color: '#34d399' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{v.owner}</span>
                    <span className="text-xs font-mono" style={{ color: '#34d399' }}>{v.parcelId}</span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{v.propertyClass} · {v.zone} · {v.county}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold" style={{ color: '#34d399' }}>${v.totalAssessedValue.toLocaleString()}</div>
                  <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Tax: ${v.annualTaxLiability.toLocaleString()}/yr</div>
                </div>
                {isExpanded ? <ChevronUp size={16} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--dark-muted)' }} />}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
                  <div className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    {[
                      ['Land Area', `${v.landArea.toLocaleString()} m²`, '#f97316'],
                      ['Building Area', `${v.buildingArea.toLocaleString()} m²`, '#60a5fa'],
                      ['Base Rate', `$${v.baseRate}/m²`, '#f59e0b'],
                      ['Zone Multiplier', `×${v.zoneMultiplier}`, '#a78bfa'],
                      ['Class Multiplier', `×${v.classMultiplier}`, '#f97316'],
                      ['Depreciation', `${(v.depreciationFactor * 100).toFixed(0)}%`, '#f59e0b'],
                      ['Land Value', `$${v.landValue.toLocaleString()}`, '#34d399'],
                      ['Building Value', `$${v.buildingValue.toLocaleString()}`, '#34d399'],
                      ['Total Assessed', `$${v.totalAssessedValue.toLocaleString()}`, '#34d399'],
                      ['Tax Rate', `${(v.taxRate * 100).toFixed(1)}%`, '#60a5fa'],
                      ['Annual Tax', `$${v.annualTaxLiability.toLocaleString()}`, '#60a5fa'],
                      ['Last Valuation', v.lastValuation, 'var(--dark-muted)'],
                    ].map(([label, val, color]) => (
                      <div key={label as string} className="p-2 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                        <div style={{ color: 'var(--dark-muted)' }}>{label}</div>
                        <div className="font-semibold mt-0.5" style={{ color: color as string }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs p-2 rounded-lg" style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)', color: '#34d399' }}>
                    Method: {v.valuationMethod} · Valuation Date: {v.lastValuation}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
